/**
 * Agent Builder Dev View - For local development and testing
 *
 * This component provides a standalone testing environment for the Agent Builder.
 * Uses Keycloak authentication via proxy to dfdev cluster.
 *
 * Usage: Access via /builder when running the dev server
 *
 * Prerequisites:
 * - Port forward to WFM: kubectl --context=eureka-flows-dfdev port-forward -n eureka-workflows svc/workflow-manager-service 8005:8005
 * - Port forward to MCP Manager or run locally on 10010
 */

import React, { useEffect, useState } from 'react';
import { AgentBuilderPage } from '@/components/AgentBuilderPage';
import { initAuth } from '@/lib/auth';
import { setIdentity } from '@/lib/config/services';
import { setApiBaseUrl, setMcpBaseUrl } from '@/lib/apiClient';

// Import styles
import '@/styles/globals.css';

/**
 * Default test user for dev mode - using real dfdev cluster credentials
 */
const DEV_USER = {
  email: 'anuj.agarwal@symphonyai.com',
  name: 'Anuj Agarwal',
  tenant_id: 'demo',
};

// ClaudeProject from dfdev cluster
const DEV_PROJECT_ID = '7fec0115-8408-42ea-9f52-4621a299efb2';

// Keycloak config for dfdev cluster
const KEYCLOAK_CONFIG = {
  url: 'https://eurekaflow-dfdev-kc.accelerate.symphonyai.com',
  realm: 'demo',
  clientId: 'workflow-canvas',
};

// Token storage keys
const DEV_TOKEN_KEY = 'agent-builder-dev-token';
const DEV_TOKEN_EXPIRY_KEY = 'agent-builder-dev-token-expiry';
const DEV_TOKEN_USER_KEY = 'agent-builder-dev-user';

/**
 * Fetch a fresh token from Keycloak
 */
async function fetchKeycloakToken(username: string, password: string): Promise<string> {
  const tokenUrl = `${KEYCLOAK_CONFIG.url}/realms/${KEYCLOAK_CONFIG.realm}/protocol/openid-connect/token`;

  const response = await fetch(tokenUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({
      grant_type: 'password',
      client_id: KEYCLOAK_CONFIG.clientId,
      username,
      password,
    }),
  });

  if (!response.ok) {
    throw new Error(`Keycloak login failed: ${response.statusText}`);
  }

  const data = await response.json();
  return data.access_token;
}

/**
 * Get stored token and username if still valid
 */
function getStoredCredentials(): { token: string; username: string } | null {
  const token = localStorage.getItem(DEV_TOKEN_KEY);
  const expiry = localStorage.getItem(DEV_TOKEN_EXPIRY_KEY);
  const username = localStorage.getItem(DEV_TOKEN_USER_KEY);

  if (token && expiry && username) {
    const expiryTime = parseInt(expiry, 10);
    if (Date.now() < expiryTime - 60000) {
      // Still valid (with 1 minute buffer)
      return { token, username };
    }
  }

  return null;
}

/**
 * Store token with expiry and username
 */
function storeCredentials(token: string, username: string, expiresIn: number = 300): void {
  localStorage.setItem(DEV_TOKEN_KEY, token);
  localStorage.setItem(DEV_TOKEN_EXPIRY_KEY, String(Date.now() + expiresIn * 1000));
  localStorage.setItem(DEV_TOKEN_USER_KEY, username);
}

export interface AgentBuilderDevViewProps {
  /**
   * Initial agent ID to load for editing (optional)
   */
  initialAgentId?: string;
}

/**
 * AgentBuilderDevView Component
 *
 * Standalone test environment for the Agent Builder.
 * Used for local development without full backend setup.
 */
export const AgentBuilderDevView: React.FC<AgentBuilderDevViewProps> = ({
  initialAgentId,
}) => {
  const [identityReady, setIdentityReady] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [loginCredentials, setLoginCredentials] = useState({
    username: 'automation_testuser@symphonyai.com',
    password: 'Welcome@2025',
  });
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // API URLs - adjust these for your environment
  const apiBaseUrl = '/api/flows-alpha-agent';
  const mcpBaseUrl = '/api/flows-alpha-mcp';

  // Token provider for external auth mode
  const tokenRef = React.useRef<string | null>(null);

  // Note: Identity is set after successful login in handleLogin or when restoring stored credentials
  // This ensures the identity matches the actual logged-in user

  // Check for existing token or show login
  useEffect(() => {
    const stored = getStoredCredentials();
    if (stored) {
      console.log('AgentBuilderDevView - Using stored credentials for:', stored.username);
      tokenRef.current = stored.token;

      // Set identity to match stored user
      setIdentity({
        tenantId: DEV_USER.tenant_id,
        projectId: DEV_PROJECT_ID,
        userId: stored.username,
      });
      setIdentityReady(true);

      initAuth({
        mode: 'external',
        tokenProvider: async () => tokenRef.current || '',
      });
      setAuthReady(true);
    } else {
      setShowLoginForm(true);
    }
  }, []);

  // Handle login
  const handleLogin = async () => {
    setIsLoggingIn(true);
    setAuthError(null);

    try {
      console.log('AgentBuilderDevView - Logging in...');
      const token = await fetchKeycloakToken(
        loginCredentials.username,
        loginCredentials.password
      );

      // Store token and username
      storeCredentials(token, loginCredentials.username, 300); // 5 minutes
      tokenRef.current = token;

      // Update identity to match logged-in user
      setIdentity({
        tenantId: DEV_USER.tenant_id,
        projectId: DEV_PROJECT_ID,
        userId: loginCredentials.username, // Use the actual logged-in user
      });
      setIdentityReady(true);
      console.log('AgentBuilderDevView - Identity updated for logged-in user:', loginCredentials.username);

      // Initialize auth with external token provider
      initAuth({
        mode: 'external',
        tokenProvider: async () => tokenRef.current || '',
      });

      setShowLoginForm(false);
      setAuthReady(true);
      console.log('AgentBuilderDevView - Login successful');
    } catch (error) {
      console.error('AgentBuilderDevView - Login failed:', error);
      setAuthError(error instanceof Error ? error.message : 'Login failed');
    } finally {
      setIsLoggingIn(false);
    }
  };

  // Initialize API URLs
  useEffect(() => {
    setApiBaseUrl(apiBaseUrl);
    setMcpBaseUrl(mcpBaseUrl);
  }, [apiBaseUrl, mcpBaseUrl]);

  const handleSave = (result: { workflow_id: string; agent_id: string; name: string }) => {
    console.log('Agent saved:', result);
    alert(`Agent saved successfully!\n\nName: ${result.name}\nAgent ID: ${result.agent_id}\nWorkflow ID: ${result.workflow_id}`);
  };

  const handleCancel = () => {
    console.log('Agent builder cancelled');
    // In a real app, this would navigate away
  };

  // Show login form
  if (showLoginForm) {
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#1a1a1a',
          color: '#fff',
          gap: '16px',
        }}
      >
        <h2 style={{ margin: 0 }}>Agent Builder Dev Login</h2>
        <p style={{ color: '#888', margin: 0, fontSize: '14px' }}>
          Login to dfdev Keycloak cluster
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '300px' }}>
          <input
            type="text"
            placeholder="Username"
            value={loginCredentials.username}
            onChange={(e) =>
              setLoginCredentials((prev) => ({ ...prev, username: e.target.value }))
            }
            style={{
              padding: '10px 12px',
              backgroundColor: '#2a2a2a',
              border: '1px solid #444',
              borderRadius: '4px',
              color: '#fff',
              fontSize: '14px',
            }}
          />
          <input
            type="password"
            placeholder="Password"
            value={loginCredentials.password}
            onChange={(e) =>
              setLoginCredentials((prev) => ({ ...prev, password: e.target.value }))
            }
            onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
            style={{
              padding: '10px 12px',
              backgroundColor: '#2a2a2a',
              border: '1px solid #444',
              borderRadius: '4px',
              color: '#fff',
              fontSize: '14px',
            }}
          />

          {authError && (
            <div style={{ color: '#ff6b6b', fontSize: '12px' }}>{authError}</div>
          )}

          <button
            onClick={handleLogin}
            disabled={isLoggingIn}
            style={{
              padding: '10px 16px',
              backgroundColor: isLoggingIn ? '#444' : '#0066cc',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: isLoggingIn ? 'not-allowed' : 'pointer',
              fontSize: '14px',
            }}
          >
            {isLoggingIn ? 'Logging in...' : 'Login'}
          </button>
        </div>
      </div>
    );
  }

  if (!identityReady || !authReady) {
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#1a1a1a',
          color: '#666',
          gap: '8px',
        }}
      >
        <div>Initializing dev environment...</div>
        <div style={{ fontSize: '12px' }}>
          {!authReady ? 'Setting up authentication...' : 'Setting up identity...'}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        height: '100vh',
        width: '100vw',
        overflow: 'hidden',
      }}
    >
      <AgentBuilderPage
        apiBaseUrl={apiBaseUrl}
        mcpBaseUrl={mcpBaseUrl}
        projectId={DEV_PROJECT_ID}
        tenantId={DEV_USER.tenant_id}
        userId={DEV_USER.email}
        userEmail={DEV_USER.email}
        initialAgentId={initialAgentId}
        getToken={async () => tokenRef.current || null}
        onSave={handleSave}
        onCancel={handleCancel}
        initialConfig={{
          name: 'My Test Agent',
          description: 'A test agent created in the builder',
          instructions: 'You are a helpful assistant that helps users with their tasks.',
        }}
      />
    </div>
  );
};

export default AgentBuilderDevView;
